import { ChangeEvent, useState } from 'react';

import { useField } from 'formik';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack';
import Menu from '@mui/material/Menu/Menu';
import { MdMoreVert } from 'react-icons/md';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';

import { SavedCard } from '../../../../../../contracts';
import { InputField } from '../../../../../InputField/InputField';
import { useAppConfig } from '../../../../../../hooks/use-app-config';
import { DIGITS_ONLY_REGEX, checkIfCardExpired, getIconByCardType } from '../../utils';
import {
  TrashCanIcon,
  CircleCheckIcon,
  CardSecurityCodeIcon,
} from '../../../../../../assets/icons';
import { getIsSupercellTemplate } from '../../../../../../utils/get-is-supercell-template';

import './styles.scss';

const IS_SUPERCELL_TEMPLATE = getIsSupercellTemplate();

interface SavedCardProps {
  isSelected: boolean;
  creditCard: SavedCard;
  onCardSelect: () => void;
  isSecurityCodeRequired?: boolean;
  onCardRemoved: (creditCard: SavedCard) => void;
}

export function SavedCreditCard({
  creditCard,
  isSelected,
  onCardSelect,
  onCardRemoved,
  isSecurityCodeRequired,
}: SavedCardProps) {
  const [, meta, helpers] = useField('securityCode');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { theme } = useAppConfig();

  const handleOptionsClick = (event?: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: any) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const handleRemoveCardClick = (event?: any) => {
    onCardRemoved(creditCard);
    handleClose(event);
  };

  const onSecurityCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const shouldPreventInputUpdate = !value.match(DIGITS_ONLY_REGEX);
    if (shouldPreventInputUpdate) {
      helpers.setValue(meta.value);
    }
  };

  const open = Boolean(anchorEl);
  const isCardExpired = checkIfCardExpired(creditCard.expMonth, creditCard.expYear);
  return (
    <Stack
      key={creditCard.pspCardIdentifier}
      className={`saved-credit-card ${isSelected ? 'expanded' : ''}`}
      style={{ borderColor: isSelected ? theme?.button.color : '' }}
      onClick={() => !isCardExpired && onCardSelect()}
    >
      <Box className="top">
        <Box className="pay-with-section">
          <Box className="pay-with-text">{t('payWith')}</Box>
          <Box className="image">{getIconByCardType(creditCard.brand)}</Box>
          <Box className="card-number">{creditCard.cardNumber}</Box>
        </Box>
        <Stack
          direction="row"
          spacing={2}
          className="options"
          alignItems={'center'}
          onClick={handleOptionsClick}
        >
          {isSelected && <CircleCheckIcon color={theme?.button.color} />}
          {isCardExpired && <Box className="remove-text">{t('expired')}</Box>}
          <MdMoreVert height={24} />
        </Stack>
        <Menu open={open} onClose={handleClose} anchorEl={anchorEl}>
          <MenuItem onClick={handleRemoveCardClick}>
            <TrashCanIcon />
            <Box className="remove-text">{t('remove')}</Box>
          </MenuItem>
        </Menu>
      </Box>
      {isSelected && isSecurityCodeRequired && (
        <Stack
          className="bottom"
          direction={'row'}
          alignItems={'center'}
          spacing={1.5}
          pt={1.5}
          width={'100%'}
        >
          <Box className="security-check-text" style={{ width: '60px' }}>
            {t('cardSecurityCode')}
          </Box>
          <Box className="input">
            <InputField
              name="securityCode"
              size="small"
              icon={<CardSecurityCodeIcon />}
              inputProps={{
                onChange: onSecurityCodeChange,
                placeholder: 'CVV',
                maxLength: 4,
              }}
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
